// @ts-nocheck
/**
 * ツインキーシステム - 鍵選択画面
 * @module twinKey/ui/screens/KeySelectionScreen
 */

import React, { useState, useMemo } from 'react';
import { useTwinKeyContext } from '../../context/TwinKeyContext';
import { useTwinKey } from '../../hooks';
import { filterCardsByAttribute, filterCardsByRarity, sortCards } from '../../core/keySystem';
import styles from '../styles/twinKeyStyles';

const ATTRIBUTES = ['すべて', '炎', '水', '光', '闇', '原始', '未来', 'なし'];
const RARITIES = ['すべて', 'C', 'UC', 'R', 'SR', 'UR', 'LR', 'SLR', 'GLR', 'XLR', 'GR'];
const SORT_OPTIONS = [
  { value: 'name', label: '名前順' },
  { value: 'attribute', label: '属性順' },
  { value: 'rarity', label: 'レアリティ順' },
  { value: 'cost', label: 'コスト順' },
];

/**
 * 鍵選択画面
 */
export function KeySelectionScreen() {
  const { state, allCards, playerData } = useTwinKeyContext();
  const { selectKey, goToHub } = useTwinKey();

  // フィルター状態
  const [attributeFilter, setAttributeFilter] = useState('すべて');
  const [rarityFilter, setRarityFilter] = useState('すべて');
  const [sortBy, setSortBy] = useState('name');
  const [searchText, setSearchText] = useState('');

  // 現在選択中のスロット（1 or 2）
  const [selectingSlot, setSelectingSlot] = useState(state.key1 ? 2 : 1);

  // 所持カードから選択可能なカードを取得
  const availableCards = useMemo(() => {
    if (!allCards || !playerData?.collection) return [];

    // 所持カードを取得（レアリティ付き）
    const ownedCards = [];
    const collection = playerData.collection || [];

    // コレクションは配列形式: [{ cardId, rarity, quantity }, ...]
    collection.forEach((item) => {
      if (item.quantity <= 0) return;

      const { cardId, rarity } = item;
      const cardIdRarity = `${cardId}_${rarity}`;

      const baseCard = allCards.find(c => c.id === cardId);
      if (baseCard && baseCard.type === 'monster') {
        ownedCards.push({
          ...baseCard,
          rarity,
          uniqueKey: cardIdRarity,
        });
      }
    });

    return ownedCards;
  }, [allCards, playerData]);

  // フィルタリング・ソート済みカード
  const filteredCards = useMemo(() => {
    let cards = [...availableCards];

    // 属性フィルター
    if (attributeFilter !== 'すべて') {
      cards = filterCardsByAttribute(cards, attributeFilter);
    }

    // レアリティフィルター
    if (rarityFilter !== 'すべて') {
      cards = filterCardsByRarity(cards, rarityFilter);
    }

    // 検索フィルター
    if (searchText) {
      const search = searchText.toLowerCase();
      cards = cards.filter(card =>
        card.name.toLowerCase().includes(search) ||
        (card.category && card.category.toLowerCase().includes(search))
      );
    }

    // ソート
    cards = sortCards(cards, sortBy);

    return cards;
  }, [availableCards, attributeFilter, rarityFilter, searchText, sortBy]);

  // カード選択ハンドラ
  const handleCardSelect = (card) => {
    if (selectingSlot === 1) {
      selectKey(1, card);
      if (!state.key2) {
        setSelectingSlot(2);
      }
    } else {
      selectKey(2, card);
    }
  };

  // 選択中かどうかをチェック
  const isSelected = (card) => {
    return (
      (state.key1?.uniqueKey === card.uniqueKey) ||
      (state.key2?.uniqueKey === card.uniqueKey)
    );
  };

  return (
    <div style={styles.keySelectionContainer}>
      {/* ヘッダー部分（固定） */}
      <div style={styles.keySelectionHeader}>
        {/* タイトルバー */}
        <div style={styles.keySelectionTitleBar}>
          <button style={styles.backButton} onClick={goToHub}>
            ← 戻る
          </button>
          <div style={styles.keySelectionTitleCenter}>
            <h1 style={styles.keySelectionTitle}>鍵の選択</h1>
            <div style={styles.subtitle}>
              鍵{selectingSlot}を選択中
            </div>
          </div>
          <div style={styles.hubHeaderSpacer} />
        </div>

        {/* 選択済み鍵表示 */}
        <div style={styles.selectedKeysBar}>
          <SelectedKeySlot
            keyCard={state.key1}
            slotNumber={1}
            isActive={selectingSlot === 1}
            onClick={() => setSelectingSlot(1)}
            onClear={() => selectKey(1, null)}
          />
          <div style={styles.keyPlusIconSmall}>+</div>
          <SelectedKeySlot
            keyCard={state.key2}
            slotNumber={2}
            isActive={selectingSlot === 2}
            onClick={() => setSelectingSlot(2)}
            onClear={() => selectKey(2, null)}
          />
        </div>

        {/* フィルターバー */}
        <div style={styles.filterBar}>
          {/* 検索 */}
          <input
            type="text"
            placeholder="カード名で検索..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={styles.searchInput}
          />

          {/* 属性フィルター */}
          <select
            value={attributeFilter}
            onChange={(e) => setAttributeFilter(e.target.value)}
            style={styles.filterSelect}
          >
            {ATTRIBUTES.map(attr => (
              <option key={attr} value={attr}>{attr}</option>
            ))}
          </select>

          {/* レアリティフィルター */}
          <select
            value={rarityFilter}
            onChange={(e) => setRarityFilter(e.target.value)}
            style={styles.filterSelect}
          >
            {RARITIES.map(r => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>

          {/* ソート */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={styles.filterSelect}
          >
            {SORT_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* カードグリッド（スクロール可能） */}
      <div style={styles.keySelectionScrollContent}>
        <div style={styles.cardGrid}>
          {filteredCards.length === 0 ? (
            <div style={styles.emptyMessage}>
              条件に合うカードがありません
            </div>
          ) : (
            filteredCards.map(card => (
              <KeyCardItem
                key={card.uniqueKey}
                card={card}
                isSelected={isSelected(card)}
                onClick={() => handleCardSelect(card)}
              />
            ))
          )}
        </div>
      </div>

      {/* 確定ボタン（フッター固定） */}
      {state.key1 && state.key2 && (
        <div style={styles.keySelectionFooter}>
          <button style={{...styles.primaryButton, width: '100%', maxWidth: '300px'}} onClick={goToHub}>
            ✓ 選択を確定
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * 選択済み鍵スロット
 */
function SelectedKeySlot({ keyCard, slotNumber, isActive, onClick, onClear }) {
  return (
    <div
      style={{
        ...styles.selectedKeySlot,
        ...(isActive ? styles.activeKeySlot : {}),
      }}
      onClick={onClick}
    >
      {keyCard ? (
        <>
          <div
            style={{
              ...styles.selectedKeyIcon,
              background: styles.getAttributeColor(keyCard.attribute),
            }}
          >
            {styles.getAttributeIcon(keyCard.attribute)}
          </div>
          <div style={styles.selectedKeyInfo}>
            <div style={styles.selectedKeyName}>{keyCard.name}</div>
            <div style={styles.selectedKeyRarity}>{keyCard.rarity}</div>
          </div>
          <button
            style={styles.clearButton}
            onClick={(e) => {
              e.stopPropagation();
              onClear();
            }}
          >
            ×
          </button>
        </>
      ) : (
        <div style={styles.emptySelectedKey}>
          鍵{slotNumber}
        </div>
      )}
    </div>
  );
}

/**
 * カードアイテム
 */
function KeyCardItem({ card, isSelected, onClick }) {
  const attrColor = styles.getAttributeColor(card.attribute);
  const rarityColor = styles.getRarityColor(card.rarity);

  return (
    <div
      style={{
        ...styles.keyCardItem,
        borderColor: isSelected ? rarityColor : attrColor,
        opacity: isSelected ? 0.5 : 1,
      }}
      onClick={isSelected ? undefined : onClick}
    >
      {/* 属性アイコン */}
      <div
        style={{
          ...styles.cardAttributeBadge,
          background: attrColor,
        }}
      >
        {styles.getAttributeIcon(card.attribute)}
      </div>

      {/* カード情報 */}
      <div style={styles.cardItemInfo}>
        <div
          style={{
            ...styles.cardItemName,
            color: rarityColor,
          }}
        >
          {card.name}
        </div>
        <div style={styles.cardItemMeta}>
          <span style={styles.cardItemRarity}>{card.rarity}</span>
          <span style={styles.cardItemCost}>コスト: {card.cost}</span>
        </div>
        <div style={styles.cardItemStats}>
          ATK: {card.attack} / HP: {card.hp}
        </div>
      </div>

      {/* 選択済みマーク */}
      {isSelected && (
        <div style={styles.selectedMark}>✓</div>
      )}
    </div>
  );
}

export default KeySelectionScreen;
